// D — an original architectural study board, grounded in Hong Ji's Nanzi house.
// The miniature is an interpretation of its stair / plaster / timber / skylight,
// not a survey model. The three framed photographs remain real, unwarped records.
import {BRAND_STROKES} from './ink-brand.js';
const clamp=(n,a=0,b=1)=>Math.max(a,Math.min(b,n));
const ease=n=>{n=clamp(n);return n*n*(3-2*n);};
const mix=(a,b,p)=>a+(b-a)*p;

function surface(kind) {
  const canvas=document.createElement('canvas');canvas.width=canvas.height=512;
  const ctx=canvas.getContext('2d'),pixels=ctx.createImageData(512,512);
  let seed=31577;const random=()=>((seed=(1664525*seed+1013904223)>>>0)/4294967296);
  for(let y=0;y<512;y++)for(let x=0;x<512;x++){
    const u=x/512,v=y/512,r=random()-.5;
    let rgb=[216,211,199],shade=1+r*.018;
    if(kind==='oak'){
      const grain=Math.sin(u*185+Math.sin(v*7.5+u*5)*1.4)+Math.sin(u*412+v*3)*.24;
      shade=1+grain*.026+r*.022;rgb=[172,131,86];
    }else if(kind==='ink'){rgb=[49,55,51];shade=1+r*.036;}
    else if(kind==='paper'){rgb=[231,225,210];shade=1+r*.025;}
    else shade+=Math.sin(x*.11+y*.023)*.004;
    const p=(y*512+x)*4;pixels.data[p]=rgb[0]*shade;pixels.data[p+1]=rgb[1]*shade;pixels.data[p+2]=rgb[2]*shade;pixels.data[p+3]=255;
  }
  ctx.putImageData(pixels,0,0);return canvas;
}
function rounded(T,w,h,d,r=.025){
  const g=new T.BoxGeometry(w,h,d,4,4,4),p=g.attributes.position;
  r=Math.min(r,w/2,h/2,d/2);const core=[w/2-r,h/2-r,d/2-r];
  for(let i=0;i<p.count;i++){
    const a=[p.getX(i),p.getY(i),p.getZ(i)],q=a.map((n,k)=>clamp(n,-core[k],core[k]));
    const delta=a.map((n,k)=>n-q[k]),l=Math.hypot(...delta)||1;
    p.setXYZ(i,q[0]+delta[0]*r/l,q[1]+delta[1]*r/l,q[2]+delta[2]*r/l);
  }
  g.computeVertexNormals();return g;
}

export async function createDWorld({THREE:T,scene,portrait=false}){
  const root=new T.Group();root.name='D / Nanzi architectural case folio';scene.add(root);
  const model=new T.Group();model.name='Interpretive Nanzi skylight cutaway';root.add(model);
  const materials=new Set(),geometries=new Set(),textures=new Set(),motions=[],documents=[];
  const previous={background:scene.background,fog:scene.fog};
  let isPortrait=!!portrait,last={assembly:0,light:0,documents:0,time:0};
  const map=kind=>{
    const t=new T.CanvasTexture(surface(kind));t.colorSpace=T.SRGBColorSpace;t.wrapS=t.wrapT=T.RepeatWrapping;t.anisotropy=4;textures.add(t);return t;
  };
  const plasterMap=map('plaster'),oakMap=map('oak'),inkMap=map('ink'),paperMap=map('paper');
  const mat=parameters=>{const m=new T.MeshStandardMaterial({roughness:.87,metalness:0,...parameters});materials.add(m);return m;};
  const plaster=mat({map:plasterMap,color:'#eeebe2',bumpMap:plasterMap,bumpScale:.004});
  const worn=mat({map:plasterMap,color:'#7c7c70',roughness:.98,bumpMap:plasterMap,bumpScale:.013});
  const oak=mat({map:oakMap,color:'#e9c493',roughness:.66,bumpMap:oakMap,bumpScale:.006});
  const ink=mat({map:inkMap,color:'#525b55',roughness:.75});
  const charcoal=mat({color:'#222a27',roughness:.69,metalness:.28});
  const paper=mat({map:paperMap,color:'#e9e3d6',roughness:1});
  const brass=mat({color:'#ac956c',roughness:.55,metalness:.60});
  function mesh(g,m,p=[0,0,0],parent=model){
    geometries.add(g);const o=new T.Mesh(g,m);o.position.set(...p);o.castShadow=true;o.receiveShadow=true;parent.add(o);return o;
  }
  const box=(w,h,d,m,p,parent=model,r=.025)=>mesh(rounded(T,w,h,d,r),m,p,parent);
  function animate(o,{from,to=o.position.toArray(),rotation=[0,0,0],fromRotation=[0,0,0],delay=0}){
    motions.push({o,from,to,rotation,fromRotation,delay});return o;
  }
  function rod(a,b,r,m,parent=model){
    const from=new T.Vector3(...a),to=new T.Vector3(...b),delta=to.clone().sub(from);
    const o=mesh(new T.CylinderGeometry(r,r,delta.length(),10),m,from.clone().add(to).multiplyScalar(.5).toArray(),parent);
    o.quaternion.setFromUnitVectors(new T.Vector3(0,1,0),delta.normalize());return o;
  }
  // A dark architectural work surface, not the interior of another generic room.
  const ground=mesh(new T.PlaneGeometry(60,60),ink,[0,-.22,0],root);ground.rotation.x=-Math.PI/2;ground.castShadow=false;
  const board=box(6.1,.20,4.8,oak,[0,0,0]);
  const base=box(5.90,.13,4.58,paper,[0,.155,0]);
  for(const [i,m]of [worn,charcoal,plaster].entries()){
    const slab=box(4.9,.085,3.8,m,[0,.29+i*.075,0]);
    animate(slab,{from:[(i-1)*1.05,.46+i*.26,.4+i*.20],delay:i*.04});
  }
  // Close to the original case: long oak door, pale lime/plaster wall, black
  // iron stair and timber treads beneath a real opening to the sky.
  const back=box(4.9,3.12,.17,plaster,[0,1.98,-1.77]);
  animate(back,{from:[-.9,.58,-1.1],fromRotation:[-Math.PI/2,0,-.07],delay:.08});
  const left=box(.18,3.12,3.62,plaster,[-2.38,1.98,0]);
  animate(left,{from:[-3.0,.53,.42],fromRotation:[0,0,Math.PI/2],delay:.15});
  const returnWall=box(.18,2.04,1.22,worn,[2.37,1.45,-1.12]);
  animate(returnWall,{from:[3.4,.50,-.5],fromRotation:[0,0,-Math.PI/2],delay:.05});
  const door=box(1.13,2.35,.085,oak,[-1.18,1.61,-1.63]);
  animate(door,{from:[-3.7,.66,.72],fromRotation:[-Math.PI/2,.28,0],delay:.23});
  const doorFrame=new T.Group();model.add(doorFrame);
  box(.075,2.49,.15,oak,[-1.80,1.65,-1.58],doorFrame);
  box(.075,2.49,.15,oak,[-.56,1.65,-1.58],doorFrame);
  box(1.31,.075,.15,oak,[-1.18,2.87,-1.58],doorFrame);
  animate(doorFrame,{from:[-2.6,0,.8],to:[0,0,0],fromRotation:[0,.22,0],delay:.28});
  rod([-.75,1.52,-1.53],[-.60,1.52,-1.53],.025,charcoal);
  for(let i=0;i<9;i++){
    const p=[1.12,.53+i*.295,1.38-i*.36];
    const steel=box(1.46,.055,.345,charcoal,[p[0],p[1]-.045,p[2]]);
    const tread=box(1.42,.075,.34,oak,p);
    for(const o of [steel,tread])animate(o,{from:[3.5+i*.10,.65+i*.11,1.80-i*.10],fromRotation:[0,.24,0],delay:.24+i*.035});
  }
  const rail=new T.Group();model.add(rail);
  for(const i of [0,2,4,6,8]){
    const y=.53+i*.295,z=1.38-i*.36;rod([1.87,y,z],[1.87,y+.77,z],.017,charcoal,rail);
  }
  rod([1.87,1.30,1.38],[1.87,3.66,-1.50],.035,oak,rail);
  rod([.39,.49,1.56],[.39,2.98,-1.53],.038,charcoal,rail);
  rod([1.82,.49,1.56],[1.82,2.98,-1.53],.038,charcoal,rail);
  animate(rail,{from:[2.7,.30,.4],to:[0,0,0],fromRotation:[0,.18,0],delay:.46});
  // The roof is open. Repeated iron members cast directional bands on plaster.
  const roof=new T.Group();model.add(roof);
  for(const x of [-2.4,2.4])box(.10,.10,3.8,charcoal,[x,3.62,0],roof);
  for(const z of [-1.86,1.86])box(4.86,.10,.10,charcoal,[0,3.62,z],roof);
  for(let i=0;i<5;i++)box(4.82,.085,.055,charcoal,[0,3.62,-1.36+i*.67],roof);
  animate(roof,{from:[0,2.9,.1],to:[0,0,0],fromRotation:[0,0,-.10],delay:.39});
  // Fine physical registration marks make the model read as a designer's folio.
  for(const x of [-2.74,2.74])for(const z of [-2.08,2.08]){
    box(.30,.006,.012,brass,[x,.228,z]);box(.012,.006,.26,brass,[x,.228,z]);
  }
  function label(text,w,h,{color='#6b675a',background='#e5decd',font=38}={}){
    const c=document.createElement('canvas');c.width=1024;c.height=Math.round(1024*h/w);
    const ctx=c.getContext('2d');ctx.fillStyle=background;ctx.fillRect(0,0,c.width,c.height);
    ctx.fillStyle=color;ctx.font=`400 ${font}px HJSans, sans-serif`;ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillText(text,c.width/2,c.height/2,c.width*.93);
    const texture=new T.CanvasTexture(c);texture.colorSpace=T.SRGBColorSpace;texture.anisotropy=4;textures.add(texture);
    const m=new T.MeshBasicMaterial({map:texture,toneMapped:false});materials.add(m);return mesh(new T.PlaneGeometry(w,h),m,[0,0,0]);
  }
  const nameplate=label('宏稷｜楠梓老屋翻修',3.1,.28,{font:48});nameplate.position.set(-.30,.235,2.10);nameplate.rotation.x=-Math.PI/2;nameplate.castShadow=false;
  const projectplate=label('黑鐵梯  ×  原木踏階  ×  天井採光',3.1,.18,{font:31});projectplate.position.set(-.30,.236,1.86);projectplate.rotation.x=-Math.PI/2;projectplate.castShadow=false;

  const photoDefinitions=[
    {id:8,file:'/assets/d/nanzi-completed-light.webp',aspect:1800/1201,width:3.20,label:'楠梓老屋｜完工實景',slot:'main'},
    {id:68,file:'/assets/d/nanzi-construction.webp',aspect:720/960,width:1.16,label:'立面施工紀錄',slot:'construction'},
    {id:49,file:'/assets/d/nanzi-skylight-detail.webp',aspect:1201/1800,width:1.16,label:'天井光影細節',slot:'detail'}
  ];
  const loader=new T.TextureLoader();
  const photos=await Promise.all(photoDefinitions.map(async def=>{
    const texture=await loader.loadAsync(def.file);texture.colorSpace=T.SRGBColorSpace;texture.anisotropy=4;textures.add(texture);
    const pivot=new T.Group();pivot.name=`Real case photograph ${def.id} / ${def.slot}`;root.add(pivot);
    const h=def.width/def.aspect,wholeH=h+.37,leaf=new T.Group();pivot.add(leaf);
    const backing=box(def.width+.18,wholeH+.15,.075,oak,[0,wholeH/2,0],leaf,.018);
    box(def.width+.11,wholeH+.08,.016,paper,[0,wholeH/2,.046],leaf,.008);
    const imageMat=new T.MeshBasicMaterial({map:texture,toneMapped:false});materials.add(imageMat);
    const image=mesh(new T.PlaneGeometry(def.width,h),imageMat,[0,h/2+.30,.058],leaf);image.castShadow=false;
    const caption=label(def.label,def.width,.22,{font:def.width>2?40:27});
    model.remove(caption);leaf.add(caption);caption.position.set(0,.15,.060);caption.castShadow=false;
    const item={...def,pivot,leaf,backing,height:wholeH};documents.push(item);return item;
  }));
  // Complete original silhouette: opacity only, never a one-stroke reconstruction.
  const calligraphy=document.createElement('canvas');calligraphy.width=1680;calligraphy.height=780;
  const inkContext=calligraphy.getContext('2d');inkContext.scale(2,2);inkContext.fillStyle='#e4dac2';
  for(const stroke of BRAND_STROKES)inkContext.fill(new Path2D(stroke.outline));
  const logoTexture=new T.CanvasTexture(calligraphy);logoTexture.colorSpace=T.SRGBColorSpace;textures.add(logoTexture);
  const logoMat=new T.MeshBasicMaterial({map:logoTexture,transparent:true,opacity:0,depthTest:false,depthWrite:false,toneMapped:false});materials.add(logoMat);
  const identity=mesh(new T.PlaneGeometry(1,390/840),logoMat,[0,0,0],root);identity.renderOrder=30;identity.castShadow=false;

  const sun=new T.DirectionalLight('#ffe5bb',3.3);sun.position.set(-4.5,10.8,4.6);sun.target.position.set(-1.4,.20,-.8);sun.castShadow=true;
  sun.shadow.mapSize.set(portrait?1024:2048,portrait?1024:2048);sun.shadow.camera.left=-12;sun.shadow.camera.right=12;sun.shadow.camera.top=12;sun.shadow.camera.bottom=-12;sun.shadow.camera.near=.1;sun.shadow.camera.far=40;sun.shadow.normalBias=.022;sun.shadow.bias=-.00008;sun.shadow.radius=3;
  const hemi=new T.HemisphereLight('#e2e9e7','#665847',.78),fill=new T.DirectionalLight('#f3eee3',.48);fill.position.set(7,5,11);
  root.add(sun,sun.target,hemi,fill);
  scene.background=new T.Color('#171c19');scene.fog=new T.FogExp2('#171c19',.014);
  const v3=new T.Vector3();
  function layout(){
    model.position.set(isPortrait?0:-2.25,.0,0);model.scale.setScalar(isPortrait?.79:1);
    for(const doc of documents){
      if(isPortrait){
        const main=doc.slot==='main';doc.pivot.position.set(main?0:doc.slot==='construction'?-1.36:1.38,main?3.47:3.41,main?-2.10:-2.42);
        doc.pivot.scale.setScalar(main?1.17:.70);doc.pivot.rotation.y=main?.08:doc.slot==='construction'?.18:-.15;
        doc.pivot.visible=main; // Portrait preserves the whole main photograph instead of shrinking three into strips.
      }else{
        doc.pivot.scale.setScalar(1);doc.pivot.visible=true;
        doc.pivot.position.set(doc.slot==='main'?2.38:doc.slot==='construction'?.22:4.73,.24,doc.slot==='main'?.0:doc.slot==='construction'?1.06:.30);
        doc.pivot.rotation.y=doc.slot==='main'?-.10:doc.slot==='construction'?.14:-.19;
      }
      doc.baseY=doc.pivot.position.y;
    }
  }
  function update({assembly=0,light=0,documents:documentProgress=0,time=0,brand=0,camera=null}={}){
    last={assembly:clamp(assembly),light:clamp(light),documents:clamp(documentProgress),time};
    for(const motion of motions){
      const p=ease((last.assembly-motion.delay)/Math.max(.01,1-motion.delay));
      motion.o.position.set(...motion.from.map((n,k)=>mix(n,motion.to[k],p)));
      motion.o.rotation.set(...motion.fromRotation.map((n,k)=>mix(n,motion.rotation[k],p)));
    }
    for(let i=0;i<photos.length;i++){
      const p=ease((last.documents-i*.06)/.88);photos[i].leaf.rotation.x=mix(-Math.PI/2-.04,0,p);
      photos[i].pivot.position.y=photos[i].baseY+(1-p)*.06;
    }
    sun.intensity=mix(.52,3.3,last.light);hemi.intensity=mix(.58,.83,last.light);fill.intensity=mix(.32,.52,last.light);
    sun.position.set(mix(-7.2,-4.5,last.light),10.8,mix(1.6,4.6,last.light));
    identity.visible=brand>.001;logoMat.opacity=brand;
    if(camera&&identity.visible){
      const distance=5.2,height=2*Math.tan(camera.fov*Math.PI/360)*distance;
      camera.getWorldDirection(v3);identity.position.copy(camera.position).addScaledVector(v3,distance);
      identity.position.addScaledVector(new T.Vector3(0,1,0).applyQuaternion(camera.quaternion),height*.11);
      identity.quaternion.copy(camera.quaternion);identity.scale.setScalar(height*(isPortrait?.41:.35));
    }
  }
  layout();update();
  return {
    update,
    resize(value){isPortrait=!!value;layout();},
    inspect(){return {sourceCase:'楠梓區老屋翻修',interpretation:true,notMeasuredAsBuilt:true,parts:['powdered-plaster-walls','timber-door','black-iron-stair','oak-treads','open-skylight'],photographs:photos.map(p=>({id:p.id,slot:p.slot,source:p.file,visible:p.pivot.visible,aspect:p.aspect})),assembly:last.assembly,daylight:last.light,documents:last.documents,meshCount:motions.length,geometryCount:geometries.size,textureCount:textures.size};},
    dispose(){scene.remove(root);for(const g of geometries)g.dispose();for(const m of materials)m.dispose();for(const texture of textures)texture.dispose();sun.shadow.map?.dispose();scene.background=previous.background;scene.fog=previous.fog;}
  };
}
